/**
 * REPERTÓRIO — o que os números dizem um ao outro enquanto aquecem.
 *
 * Antes desta tela, o texto do aquecimento eram nove frases dentro do código.
 * Num parque de 50 números isso é a mesma frase circulando dezenas de vezes
 * por dia entre os mesmos pares — assinatura, não vocabulário pobre.
 *
 * A tela existe porque quem escreve mensagem é quem entende de conversa, e
 * não deveria precisar de um deploy para trocar uma palavra.
 *
 * O QUE ELA MOSTRA QUE NÃO É ÓBVIO
 *
 * A contagem por tipo, no topo. Tipo com poucas frases entra em cooldown
 * antes e trava o aquecimento — o motor prefere não enviar a enviar repetido.
 * É essa contagem que diz onde vale escrever mais, e não o total geral.
 */
import { useCallback, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  Tabela,
  classeEntrada,
} from "../ui/base.js";
import {
  Conteudo,
  Gaveta,
  Grade,
  Indicadores,
  LinhaVazia,
  Td,
  useCarga,
} from "../ui/comercial.js";

interface Frase {
  id: string;
  tipo: string;
  papel: string;
  texto: string;
  tamanho: string;
  tema: string;
  registro: string;
  risco: string;
  peso: number;
  ativo: boolean;
  usos: number;
  ultimoUso: string | null;
}

/** Os tipos, com o que cada um significa na conversa entre dois chips. */
const TIPOS: ReadonlyArray<{ id: string; nome: string; desc: string }> = [
  { id: "envio", nome: "Envio", desc: "o chip manda e não cobra resposta" },
  { id: "recebimento", nome: "Recebimento", desc: "o que chega no chip que aquece" },
  { id: "interacao", nome: "Interação", desc: "par de ida e volta" },
  { id: "grupo", nome: "Grupo", desc: "mensagem em grupo interno pequeno" },
  { id: "midia", nome: "Mídia", desc: "legenda de imagem" },
  { id: "chamada", nome: "Chamada", desc: "antes, depois ou chamada perdida" },
  { id: "verificacao", nome: "Verificação", desc: "a sonda que não pode parecer sonda" },
];

const NOME_TIPO: Record<string, string> = Object.fromEntries(
  TIPOS.map((t) => [t.id, t.nome]),
);

/**
 * Abaixo disto, um tipo entra em cooldown rápido demais.
 *
 * A conta é da janela mais apertada: par+tema por 7 dias. Com poucos temas
 * distintos, o par esgota o acervo antes da semana virar e o motor passa a
 * bloquear atividade por falta de frase disponível.
 */
const MINIMO_SAUDAVEL = 12;

export function Repertorio(): JSX.Element {
  const { estado, recarregar } = useCarga(useCallback(() => api.repertorio(), []));
  const [editando, setEditando] = useState<Frase | "nova" | null>(null);
  const [filtro, setFiltro] = useState("todos");
  const [recado, setRecado] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  async function alternar(f: Frase): Promise<void> {
    setOcupado(f.id);
    setRecado(null);
    try {
      const r = await api.editarFrase(f.id, { ativo: !f.ativo });
      setRecado(r.motivo);
      recarregar();
    } catch (e: unknown) {
      setRecado(e instanceof Error ? e.message : "O servidor recusou a mudança.");
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(d) => {
          const visiveis =
            filtro === "todos" ? d.itens : d.itens.filter((f) => f.tipo === filtro);
          const magros = d.porTipo.filter((t) => t.ativas < MINIMO_SAUDAVEL);
          const semNenhuma = TIPOS.filter(
            (t) => !d.porTipo.some((x) => x.tipo === t.id && x.ativas > 0),
          );

          return (
            <div className="grid gap-4">
              <Indicadores>
                <Indicador
                  rotulo="Frases no acervo"
                  valor={d.itens.length}
                  nota={`${d.itens.filter((f) => f.ativo).length} ativa(s)`}
                />
                <Indicador
                  rotulo="Temas distintos"
                  valor={new Set(d.itens.map((f) => f.tema)).size}
                  nota="O cooldown de tema é o mais apertado: 7 dias por par"
                />
                <Indicador
                  rotulo="Já usadas"
                  valor={d.itens.filter((f) => f.usos > 0).length}
                  nota="Uso alimenta o cooldown"
                />
              </Indicadores>

              {recado && <Aviso tom="warn">{recado}</Aviso>}

              {semNenhuma.length > 0 && (
                <Aviso tom="crit">
                  <strong>
                    {semNenhuma.length} tipo(s) sem nenhuma frase ativa:
                  </strong>{" "}
                  {semNenhuma.map((t) => t.nome).join(", ")}. O motor não tem o
                  que enviar nesses tipos — a atividade é bloqueada com motivo,
                  em vez de sair repetida.
                </Aviso>
              )}

              {semNenhuma.length === 0 && magros.length > 0 && (
                <Aviso tom="warn">
                  <strong>Acervo magro em:</strong>{" "}
                  {magros
                    .map((t) => `${NOME_TIPO[t.tipo] ?? t.tipo} (${t.ativas})`)
                    .join(", ")}
                  . Abaixo de {MINIMO_SAUDAVEL} frases ativas, o par esgota o
                  acervo antes da semana virar — o cooldown de tema é de 7 dias
                  por par, e é ele que aperta primeiro.
                </Aviso>
              )}

              <Painel
                titulo="Contagem por tipo"
                dica="É esta contagem que diz onde vale escrever mais, não o total"
              >
                <div className="flex gap-1.5 flex-wrap">
                  {TIPOS.map((t) => {
                    const c = d.porTipo.find((x) => x.tipo === t.id);
                    const n = c?.ativas ?? 0;
                    return (
                      <div
                        key={t.id}
                        className="rounded-lg border border-linha px-3 py-2 min-w-[128px]"
                        title={t.desc}
                      >
                        <div className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">
                          {t.nome}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <strong className="text-[17px] tabular-nums">{n}</strong>
                          <Selo tom={n === 0 ? "crit" : n < MINIMO_SAUDAVEL ? "warn" : "ok"}>
                            {n === 0 ? "vazio" : n < MINIMO_SAUDAVEL ? "magro" : "ok"}
                          </Selo>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Painel>

              <Painel
                titulo="As frases"
                dica="Cada uma é sorteada respeitando cinco janelas de cooldown"
                acoes={
                  <div className="flex gap-2 items-center">
                    <select
                      className={`${classeEntrada} py-1 text-[12.5px]`}
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                    >
                      <option value="todos">Todos os tipos</option>
                      {TIPOS.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nome}
                        </option>
                      ))}
                    </select>
                    <Botao tipo="principal" pequeno onClick={() => setEditando("nova")}>
                      Nova frase
                    </Botao>
                  </div>
                }
                semCorpo
              >
                <Tabela
                  larguraMin={900}
                  colunas={[
                    { chave: "t", rotulo: "Texto" },
                    { chave: "ti", rotulo: "Tipo" },
                    { chave: "te", rotulo: "Tema" },
                    { chave: "p", rotulo: "Papel" },
                    { chave: "u", rotulo: "Usos", direita: true },
                    { chave: "e", rotulo: "Estado" },
                    { chave: "a", rotulo: "", direita: true },
                  ]}
                >
                  {visiveis.length === 0 ? (
                    <LinhaVazia colunas={7}>
                      Nenhuma frase neste filtro.
                    </LinhaVazia>
                  ) : (
                    visiveis.map((f) => (
                      <tr key={f.id} className="border-t border-linha">
                        <Td>
                          <span className="text-[13px] whitespace-pre-line">
                            {f.texto}
                          </span>
                        </Td>
                        <Td className="text-[12.5px] text-tinta-2 whitespace-nowrap">
                          {NOME_TIPO[f.tipo] ?? f.tipo}
                        </Td>
                        <Td className="text-[12.5px] text-tinta-2">{f.tema}</Td>
                        <Td className="text-[12px] text-tinta-3">{f.papel}</Td>
                        <Td direita className="text-[12.5px] text-tinta-2 tabular-nums">
                          {f.usos}
                        </Td>
                        <Td>
                          <Selo tom={f.ativo ? "ok" : "idle"}>
                            {f.ativo ? "ativa" : "fora"}
                          </Selo>
                        </Td>
                        <Td direita>
                          <div className="flex gap-1.5 justify-end">
                            <Botao pequeno tipo="fantasma" onClick={() => setEditando(f)}>
                              Editar
                            </Botao>
                            <Botao
                              pequeno
                              tipo="fantasma"
                              desabilitado={ocupado === f.id}
                              onClick={() => void alternar(f)}
                            >
                              {f.ativo ? "Desativar" : "Reativar"}
                            </Botao>
                          </div>
                        </Td>
                      </tr>
                    ))
                  )}
                </Tabela>
              </Painel>

              <Painel titulo="Como uma frase é escolhida" dica="Cinco janelas, todas ao mesmo tempo">
                <ul className="grid gap-1.5 text-[13px] text-tinta-2">
                  <li>
                    <strong>Par + texto, 30 dias.</strong> Estes dois números não
                    repetem esta frase entre si.
                  </li>
                  <li>
                    <strong>Par + tema, 7 dias.</strong> Nem voltam ao mesmo
                    assunto. É a janela que aperta primeiro.
                  </li>
                  <li>
                    <strong>Chip + texto, 14 dias.</strong> Este número não repete
                    a frase com ninguém.
                  </li>
                  <li>
                    <strong>Parque + texto, 6 horas.</strong> No máximo dois usos
                    em todo o parque na mesma janela.
                  </li>
                  <li>
                    <strong>Eco, sempre.</strong> O destino nunca devolve a mesma
                    frase que recebeu — eco é o padrão mais fácil de detectar numa
                    conversa de dois.
                  </li>
                </ul>
                <p className="text-[12.5px] text-tinta-3 mt-3 leading-relaxed">
                  Quando tudo está em cooldown, o motor <strong>não envia</strong>{" "}
                  e registra o motivo. Perder uma atividade custa uma linha no
                  ledger; repetir custa o número.
                </p>
              </Painel>
            </div>
          );
        }}
      </Conteudo>

      {editando && (
        <FormularioFrase
          alvo={editando === "nova" ? null : editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={(m) => {
            setEditando(null);
            setRecado(m);
            recarregar();
          }}
        />
      )}
    </div>
  );
}

function FormularioFrase({
  alvo,
  aoFechar,
  aoSalvar,
}: {
  alvo: Frase | null;
  aoFechar: () => void;
  aoSalvar: (motivo: string) => void;
}): JSX.Element {
  const [texto, setTexto] = useState(alvo?.texto ?? "");
  const [tipo, setTipo] = useState(alvo?.tipo ?? "envio");
  const [papel, setPapel] = useState(alvo?.papel ?? "solo");
  const [tema, setTema] = useState(alvo?.tema ?? "geral");
  const [registro, setRegistro] = useState(alvo?.registro ?? "informal");
  const [tamanho, setTamanho] = useState(alvo?.tamanho ?? "M");
  const [risco, setRisco] = useState(alvo?.risco ?? "baixo");
  const [peso, setPeso] = useState(alvo?.peso ?? 5);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const problema = useMemo(() => {
    if ([...texto.trim()].length < 2) return "A frase precisa de ao menos 2 caracteres.";
    if (tema.trim().length < 2) return "Dê um tema à frase — é ele que tem o cooldown mais curto.";
    return null;
  }, [texto, tema]);

  async function salvar(): Promise<void> {
    if (problema) {
      setErro(problema);
      return;
    }
    setOcupado(true);
    setErro(null);
    try {
      const corpo = {
        tipo,
        papel,
        texto: texto.trim(),
        tema: tema.trim(),
        registro,
        tamanho,
        risco,
        peso,
      };
      const r = alvo
        ? await api.editarFrase(alvo.id, corpo)
        : await api.criarFrase(corpo);
      aoSalvar(r.motivo);
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : "O servidor recusou a frase.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Gaveta
      titulo={alvo ? "Editar frase" : "Nova frase"}
      dica="Ela vai ser lida por outro número do próprio parque — e por um humano, se houver denúncia"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao
            tipo="principal"
            desabilitado={ocupado || problema !== null}
            onClick={() => void salvar()}
          >
            {ocupado ? "Salvando…" : "Salvar"}
          </Botao>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          {problema && (
            <span className="text-[12px] text-tinta-3 ml-auto max-w-[36ch]">
              {problema}
            </span>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        {erro && <Aviso tom="crit">{erro}</Aviso>}

        <Campo rotulo="Texto" dica="Quebra de linha vale: conversa real tem mensagem de 3 linhas">
          <textarea
            className={`${classeEntrada} min-h-[110px] leading-relaxed`}
            value={texto}
            placeholder="saí agora e ta 38 graus, nao da pra ficar na rua"
            onChange={(e) => setTexto(e.target.value)}
          />
        </Campo>

        <Aviso tom="cool">
          Pontuação correta demais é o defeito aqui. O que circula entre dois
          números do próprio parque precisa parecer conversa de gente — e
          conversa de gente tem letra minúscula, abreviação e vírgula faltando.
          Se houver denúncia, é este texto que o WhatsApp lê.
        </Aviso>

        <Grade colunas={2}>
          <Campo rotulo="Tipo de atividade">
            <select className={classeEntrada} value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {TIPOS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} — {t.desc}
                </option>
              ))}
            </select>
          </Campo>

          <Campo rotulo="Papel">
            <select className={classeEntrada} value={papel} onChange={(e) => setPapel(e.target.value)}>
              <option value="solo">Solo — se sustenta sozinha</option>
              <option value="abre">Abre — puxa conversa</option>
              <option value="responde">Responde — fecha um par</option>
            </select>
          </Campo>

          <Campo rotulo="Tema" dica="O cooldown de tema é de 7 dias por par">
            <input
              className={classeEntrada}
              value={tema}
              placeholder="clima, comida, deslocamento…"
              onChange={(e) => setTema(e.target.value)}
            />
          </Campo>

          <Campo rotulo="Registro">
            <select
              className={classeEntrada}
              value={registro}
              onChange={(e) => setRegistro(e.target.value)}
            >
              <option value="informal">informal</option>
              <option value="informal_pesado">informal pesado</option>
              <option value="neutro">neutro</option>
            </select>
          </Campo>

          <Campo rotulo="Tamanho">
            <select
              className={classeEntrada}
              value={tamanho}
              onChange={(e) => setTamanho(e.target.value)}
            >
              <option value="P">P — até umas 4 palavras</option>
              <option value="M">M — uma linha</option>
              <option value="G">G — 2 a 3 linhas</option>
            </select>
          </Campo>

          <Campo rotulo="Peso no sorteio" dica="1 a 10; pondera sem obrigar">
            <input
              type="number"
              min={1}
              max={10}
              className={classeEntrada}
              value={peso}
              onChange={(e) => setPeso(Number(e.target.value) || 5)}
            />
          </Campo>
        </Grade>

        <Campo
          rotulo="Risco"
          dica="Alto = frase que colide com roteiro de golpe; fica fora de parque novo"
        >
          <select className={classeEntrada} value={risco} onChange={(e) => setRisco(e.target.value)}>
            <option value="baixo">baixo — banalidade sem consequência</option>
            <option value="medio">médio</option>
            <option value="alto">alto — urgência, dinheiro, conta, código</option>
          </select>
        </Campo>
      </div>
    </Gaveta>
  );
}
